import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  CoinsIcon,
  LockIcon,
  SealCheckIcon,
  LightningIcon,
  BankIcon,
} from "@phosphor-icons/react";

export default function Payouts() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [method, setMethod] = useState(null);
  const [form, setForm] = useState({
    method_type: "paypal",
    paypal_email: "",
    bank_account_holder: "",
    bank_name: "",
    bank_routing_number: "",
    bank_account_last4: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    if (!user.account_type.endsWith("_seller")) {
      navigate("/dashboard");
      return;
    }
    Promise.all([api.get("/me/payouts"), api.get("/me/payout-method")])
      .then(([p, pm]) => {
        setItems(p.data.items);
        setTotals(p.data.totals);
        const m = pm.data.payout_method || null;
        setMethod(m);
        if (m) {
          setForm((prev) => ({ ...prev, ...m }));
        }
      })
      .finally(() => setLoading(false));
  }, [user, navigate]);

  const savePayoutMethod = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { method_type: form.method_type };
      if (form.method_type === "paypal") {
        payload.paypal_email = form.paypal_email;
      } else {
        payload.bank_account_holder = form.bank_account_holder;
        payload.bank_name = form.bank_name;
        payload.bank_routing_number = form.bank_routing_number;
        payload.bank_account_last4 = form.bank_account_last4;
      }
      const r = await api.put("/me/payout-method", payload);
      setMethod(r.data.payout_method);
      toast.success("Payout method saved");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  const progress = Math.min(100, ((totals.sales_count || 0) / (totals.threshold || 500)) * 100);

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="payouts-page">
      <Navbar />
      <main className="flex-1 max-w-6xl mx-auto w-full px-6 py-10">
        <p className="overline text-zinc-500">PAYOUTS</p>
        <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter mt-2">
          Your payouts
        </h1>

        <div className="mt-8 grid md:grid-cols-3 gap-4">
          <Card
            label="Released"
            value={`$${(totals.released || 0).toFixed(2)}`}
            icon={<SealCheckIcon size={22} className="text-emerald-600" />}
          />
          <Card
            label="Held"
            value={`$${(totals.held || 0).toFixed(2)}`}
            icon={<LockIcon size={22} className="text-amber-600" />}
          />
          <Card
            label="Lifetime sales"
            value={totals.sales_count ?? 0}
            icon={<CoinsIcon size={22} className="text-[#1D78AB]" />}
          />
        </div>

        {/* Instant payout progress */}
        <div className="mt-6 border border-zinc-200 bg-white p-6">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="flex items-center gap-2">
                <LightningIcon size={18} className="text-amber-500" weight="fill" />
                <p className="overline text-zinc-500">INSTANT PAYOUT STATUS</p>
              </div>
              <div className="font-display font-bold text-xl mt-1">
                {totals.instant_payout_enabled
                  ? "Unlocked — payouts release immediately"
                  : `${totals.sales_count || 0} / ${totals.threshold || 500} sales to unlock`}
              </div>
            </div>
            {totals.instant_payout_enabled && (
              <span className="px-3 py-1 text-[10px] font-bold tracking-widest uppercase bg-amber-500 text-white">
                INSTANT
              </span>
            )}
          </div>
          <div className="h-2 bg-zinc-100 overflow-hidden">
            <div
              className={`h-full ${
                totals.instant_payout_enabled ? "bg-amber-500" : "bg-[#1D78AB]"
              }`}
              style={{ width: `${progress}%` }}
              data-testid="payout-progress"
            />
          </div>
          <p className="text-xs text-zinc-500 mt-2">
            Until you reach {totals.threshold || 500} completed sales, payouts are held until the buyer
            confirms delivery. After that, every sale pays out instantly.
          </p>
        </div>

        {/* Payout method */}
        <div className="mt-6 card-modern p-6" data-testid="payout-method-section">
          <div className="flex items-center gap-2 mb-4">
            <BankIcon size={18} className="text-[#1D78AB]" />
            <p className="overline text-zinc-500">PAYOUT METHOD</p>
            {method && (
              <span className="pill bg-emerald-500 text-white ml-auto">
                {method.method_type === "paypal" ? "PayPal on file" : "Bank on file"}
              </span>
            )}
          </div>
          <p className="text-sm text-zinc-600 mb-4">
            Tell us where to send your released payouts. This is required before your first
            delivery-confirmed sale can be wired out.
          </p>
          <form className="grid md:grid-cols-2 gap-3" onSubmit={savePayoutMethod}>
            <div className="md:col-span-2">
              <label className="overline text-zinc-500 block mb-1">Method</label>
              <Select
                value={form.method_type}
                onValueChange={(v) => setForm((f) => ({ ...f, method_type: v }))}
              >
                <SelectTrigger className="bg-white" data-testid="payout-method-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="paypal">PayPal</SelectItem>
                  <SelectItem value="bank">Bank transfer (ACH)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.method_type === "paypal" ? (
              <div className="md:col-span-2">
                <label className="overline text-zinc-500 block mb-1">PayPal email</label>
                <Input
                  type="email"
                  value={form.paypal_email || ""}
                  onChange={(e) => setForm((f) => ({ ...f, paypal_email: e.target.value }))}
                  placeholder="you@paypal.com"
                  className="rounded-full h-11 px-4"
                  required
                  data-testid="payout-paypal-email"
                />
              </div>
            ) : (
              <>
                <div>
                  <label className="overline text-zinc-500 block mb-1">Account holder</label>
                  <Input
                    value={form.bank_account_holder || ""}
                    onChange={(e) => setForm((f) => ({ ...f, bank_account_holder: e.target.value }))}
                    placeholder="Jane Doe"
                    className="rounded-full h-11 px-4"
                    required
                    data-testid="payout-bank-holder"
                  />
                </div>
                <div>
                  <label className="overline text-zinc-500 block mb-1">Bank name</label>
                  <Input
                    value={form.bank_name || ""}
                    onChange={(e) => setForm((f) => ({ ...f, bank_name: e.target.value }))}
                    placeholder="Chase"
                    className="rounded-full h-11 px-4"
                    required
                    data-testid="payout-bank-name"
                  />
                </div>
                <div>
                  <label className="overline text-zinc-500 block mb-1">Routing number</label>
                  <Input
                    value={form.bank_routing_number || ""}
                    onChange={(e) => setForm((f) => ({ ...f, bank_routing_number: e.target.value }))}
                    placeholder="021000021"
                    className="rounded-full h-11 px-4"
                    required
                    data-testid="payout-bank-routing"
                  />
                </div>
                <div>
                  <label className="overline text-zinc-500 block mb-1">Account last 4</label>
                  <Input
                    value={form.bank_account_last4 || ""}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        bank_account_last4: e.target.value.replace(/\D/g, "").slice(0, 4),
                      }))
                    }
                    placeholder="1234"
                    className="rounded-full h-11 px-4"
                    required
                    data-testid="payout-bank-last4"
                  />
                </div>
              </>
            )}
            <div className="md:col-span-2">
              <Button
                type="submit"
                disabled={saving}
                className="bg-[#1D78AB] hover:bg-[#165d85] text-white h-11 px-6 rounded-full font-semibold shadow-lg shadow-[#1D78AB]/20"
                data-testid="payout-save-btn"
              >
                {saving ? "Saving…" : method ? "Update payout method" : "Save payout method"}
              </Button>
            </div>
          </form>
        </div>

        <div className="mt-10">
          <p className="overline text-zinc-500 mb-3">PAYOUT HISTORY</p>
          {loading ? (
            <p className="text-sm text-zinc-500">Loading…</p>
          ) : items.length === 0 ? (
            <div className="border border-zinc-200 p-12 text-center bg-white">
              <p className="font-display font-bold text-xl">No payouts yet.</p>
              <p className="text-sm text-zinc-600 mt-1">
                Once you make your first sale, it'll appear here.
              </p>
            </div>
          ) : (
            <div className="border border-zinc-200 divide-y divide-zinc-200 bg-white">
              {items.map((p) => (
                <Link
                  key={p.id}
                  to={`/orders/${p.order_id}`}
                  className="p-4 flex items-center gap-4 hover:bg-zinc-50"
                  data-testid={`payout-row-${p.id}`}
                >
                  <div className="flex-1">
                    <div className="font-semibold flex items-center gap-2 flex-wrap">
                      {p.kind === "referral" && (
                        <span className="pill bg-amber-500 text-white flex-shrink-0">REFERRAL</span>
                      )}
                      <span>Order {p.order_id.slice(-8).toUpperCase()}</span>
                    </div>
                    <p className="text-xs text-zinc-500">
                      {p.kind === "referral"
                        ? `Referral code ${p.referral_code || ""}`
                        : new Date(p.created_at).toLocaleString()}
                    </p>
                  </div>
                  <span
                    className={`px-2 py-0.5 text-[10px] font-bold tracking-widest uppercase ${
                      p.status === "released"
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-amber-100 text-amber-700"
                    }`}
                  >
                    {p.status}
                  </span>
                  <div className="text-right flex-shrink-0">
                    <span className="font-mono font-bold text-[#1D78AB] text-lg block">
                      ${Number(p.net_amount ?? p.amount).toFixed(2)}
                    </span>
                    {p.kind !== "referral" && Number(p.platform_fee || 0) > 0 && (
                      <span className="text-[10px] text-zinc-400 font-mono">
                        gross ${Number(p.amount).toFixed(2)} · fee ${Number(p.platform_fee).toFixed(2)}
                      </span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Card({ label, value, icon }) {
  return (
    <div className="border border-zinc-200 bg-white p-5">
      <div className="flex items-center justify-between">
        <p className="overline text-zinc-500">{label}</p>
        {icon}
      </div>
      <div className="font-display font-black text-3xl mt-2">{value}</div>
    </div>
  );
}
